/**
 * useGrowthReminders Hook
 * Fetches, creates, updates, and deletes growth reminders with React Query mutations.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  createGrowthReminder,
  deleteGrowthReminder,
  getGrowthReminders,
  updateGrowthReminder,
} from "../services/my-rituality.service"
import type { CreateGrowthReminderRequest } from "../types/my-rituality.types"

const QUERY_KEY = ["growthReminders"]

export function useGrowthReminders() {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getGrowthReminders,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateGrowthReminderRequest) => createGrowthReminder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (reminderId: string) => deleteGrowthReminder(reminderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({
      reminderId,
      data,
    }: {
      reminderId: string
      data: { text?: string; backgroundImageKey?: string }
    }) => updateGrowthReminder(reminderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })

  return {
    reminders: query.data?.data || [],
    isLoading: query.isLoading,
    createReminder: createMutation.mutate,
    deleteReminder: deleteMutation.mutate,
    updateReminder: updateMutation.mutate,
    isCreating: createMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isUpdating: updateMutation.isPending,
  }
}
